import { $api } from "../generated/client";

/**
 * Fetches the super admin shops overview (stats stored in DB per shop).
 * Only accessible to users with services.superpowers.activated = true.
 */
export function useSuperAdminShops() {
  return $api.useQuery(
    "get",
    "/super-admin/shops",
    {},
    { refetchInterval: 60_000 },
  );
}

/** Recompute and save stats for all shops (full or incremental via maxAgeMs). */
export function useRefreshSuperAdminShops() {
  return $api.useMutation("post", "/super-admin/shops/refresh");
}

/** Recompute and save stats for a single shop. */
export function useRefreshSuperAdminShopStats() {
  return $api.useMutation("post", "/super-admin/shops/{shopId}/refresh");
}

/** Fetch per-shop detail stats (country breakdown + carriers). Reads from DB. */
export function useSuperAdminShopDetail(shopId: string | null) {
  return $api.useQuery(
    "get",
    "/super-admin/shops/{shopId}/detail",
    { params: { path: { shopId: shopId ?? "" } } },
    { enabled: !!shopId },
  );
}

/** Update manual metadata for a shop (isTestShop, clientStatus, notes…). */
export function useUpdateSuperAdminShopMeta() {
  return $api.useMutation("patch", "/super-admin/shops/{shopId}/metadata");
}

/** Reactivate a shop that was deactivated by the inactivity cron. */
export function useReactivateSuperAdminShop() {
  return $api.useMutation("post", "/super-admin/shops/{shopId}/reactivate");
}

/** Fetch catalog data (sync state + loaded counts) for all shops. Separate from main shops endpoint to avoid blocking the table load. */
export function useSuperAdminShopsCatalog() {
  return $api.useQuery(
    "get",
    "/super-admin/shops/catalog",
    {},
    { refetchInterval: 60_000 },
  );
}

/** Fetch global config flags (collection `config`, doc `production`). */
export function useSuperAdminConfig() {
  return $api.useQuery("get", "/super-admin/config");
}

/** Update global config flags. */
export function useUpdateSuperAdminConfig() {
  return $api.useMutation("patch", "/super-admin/config");
}

/**
 * Fetches a recent order of the shop still holding a non-returned item: the
 * order number to type into the shop's return portal to test the flow.
 * Lazy by design, one query per shop is too heavy to run for the whole list.
 */
export function useSuperAdminReturnableOrder(
  shopId: string,
  enabled: boolean,
) {
  return $api.useQuery(
    "get",
    "/super-admin/shops/{shopId}/returnable-order",
    { params: { path: { shopId } } },
    { enabled, staleTime: Infinity },
  );
}
